/**
 * The outline of the possible window modes.
 */
import type { PoeStateChangedCallback, SafConfig } from './Application';
import type { StoreApi } from 'zustand';
import type { BasePortletStore } from '../lib';
import type React from 'react';
import type { PoeEventParameterTypes } from './PoeEventParameterTypes';

export enum WindowMode {
  NORMAL = 'NORMAL',
  HIDDEN = 'HIDDEN',
  MINIMIZED = 'MINIMIZED',
  MAXIMIZED = 'MAXIMIZED'
}

/**
 * Outlines all possible data access modes.
 */
export enum DataAccessPermission {
  NONE = 'NONE',
  READ_ONLY = 'READ_ONLY',
  READ_WRITE = 'READ_WRITE'
}

/**
 * The state of an orchestrated component.
 */
export type ComponentState = {
  /**
   * Indicates if the component is active and is allowed to perform operations.
   */
  active: boolean;

  /**
   * The current window mode of the portlet.
   */
  windowMode: WindowMode;

  /**
   * The current access permission.
   */
  dataAccessPermission: DataAccessPermission;

  /**
   * The state changing event names.
   */
  stateChangingActions: string[];
};

export type ReactPortletExport = {
  PortletStoreApi: StoreApi<BasePortletStore<never>>;
  App: React.FunctionComponent;
  poeStateChanged: PoeStateChangedCallback;
};

/**
 * Additional configuration of an orchestrated component.
 */
export type ComponentConfig = {
  /**
   * The custom divId into which to render the component.
   */
  divId: string;
};

export type WorkflowState = {
  /**
   * the unique workflow instance ID.
   */
  workflowInstanceID: string;
  /**
   * The current workflow state.
   */
  workflowState: string;

  /**
   * The mapping from component instance ID to component state.
   */
  componentStates: Record<string, ComponentState>;

  /**
   * The mapping from component instance ID to component configuration;
   */
  componentConfigs: Record<string, ComponentConfig>;

  /**
   * Indicates if the workflow is finished.
   */
  finished: boolean;
};

/**
 * An enumeration of action outcomes, that are returned from the POE invocation.
 */
export enum ActionOutcome {
  NOT_REGISTERED = 'NOT_REGISTERED',
  NOT_ORCHESTRATED = 'NOT_ORCHESTRATED',
  SUCCESS = 'SUCCESS'
}

/**
 * The outline of operations the POE implementation provides,
 */
export interface PoeInterface {
  /**
   * Lookup the Component state for a certain component.
   * @param uniqueComponentID the unique ID of the component.
   * @return the current compontent state
   */
  getState(uniqueComponentID: string): ComponentState;
  /**
   * Adds an event listener for a certain event name.
   * @param uniqueComponentID the ID of the registered component, which the event handler should be added for.
   * @param evtName the name of the event
   * @param fn the handler function
   * @param scope the handler scope
   * @param eOpts the options for the handler function.
   */
  addEventListener(
    uniqueComponentID: string,
    evtName: string,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    fn: Function,
    scope?: unknown,
    eOpts?: unknown
  ): void;
  /**
   * Removes an event listener from a registered component.
   * @param uniqueComponentID the unique ID of the component to remove the event listener for.
   * @param evtName the name of the event to delete.
   */
  removeEventListener(uniqueComponentID: string, evtName: string): void;
  /**
   * Performs a certain action for a component, which might cause a POE state change.
   * @param uniqueComponentID the unique ID of the component that causes the action.
   * @param action the action to fire.
   * @param data the data for that action.
   * @return the ActionOutcome.
   */
  performAction(
    uniqueComponentID: string,
    action: string,
    data: unknown
  ): ActionOutcome;

  /**
   * Fire an non-statechanging event on a single component.
   *
   * This is only useful when delegating to another framework from a wrapper.
   *
   * @see PoeInterface#performAction
   *
   * @param uniqueComponentID
   * @param action
   * @param data
   */
  fireEventOnSingleComponent(
    uniqueComponentID: string,
    action: string,
    data: unknown
  ): void;

  /**
   * Builds the application for a certain component.
   * @param uniqueComponentID the unique ID of the component.
   * @param appClass the application class as dot separated string. Format: PACKAGE.PACKAGE.APPCLASS
   * @param config the configuration to supply to the component.
   * @param jsFramework Theme of the portlet to be loaded
   * @return the Orchestratable instance of the new application, if successfully created.
   */
  buildApplication(
    uniqueComponentID: string,
    appClass: string,
    config: SafConfig<unknown>,
    jsFramework: string
  ): Promise<OrchestratableInstance>;
}

/**
 * Helper method, which grants access to the POE.
 */
export function getPOE(): PoeInterface | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const wd: any = window;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const poeScope: any = wd['POE'];
  if (poeScope !== null && poeScope !== undefined) {
    return poeScope.instance;
  }
  return undefined;
}

/**
 * The handle to the component instance. Can be used to fire and capture events.
 */
export class OrchestratableInstance {
  /**
   * The unique component ID.
   */
  componentID: string;

  /**
   * indicates if the component is orchestrated.
   */
  orchestrated: boolean;

  /**
   * Constructor using fields.
   * @param componentID the component ID.
   * @param orchestrated indicates if the component is orchestrated.
   */
  constructor(componentID: string, orchestrated: boolean) {
    this.componentID = componentID;
    this.orchestrated = orchestrated;
  }

  /**
   * @return true if the component is orchestrated.
   */
  public isOrchestrated(): boolean {
    return this.orchestrated;
  }

  /**
   * @return the unique component ID.
   */
  getComponentID(): string {
    return this.componentID;
  }
  /**
   * @return The current ComponentState of the component.
   */
  public getState(): ComponentState {
    const poe: PoeInterface | undefined = getPOE();
    if (undefined === poe) {
      return {
        active: true,
        windowMode: WindowMode.NORMAL,
        dataAccessPermission: DataAccessPermission.READ_WRITE,
        stateChangingActions: []
      };
    } else {
      return poe.getState(this.componentID);
    }
  }

  /**
   * @return indicates if the component is currently active.
   */
  isActive(): boolean {
    return this.getState().active;
  }

  /**
   * @return the current window mode.
   */
  getWindowMode(): WindowMode {
    return this.getState().windowMode;
  }

  /**
   * Adds an event listener to this component.
   * @param evtName the name of the event.
   * @param fn the handler function,
   * @param scope the scope of the handler function.
   * @param eOpts the additional options, that shall be available in the callback.
   */
  addEventListener<E extends keyof PoeEventParameterTypes>(
    evtName: E,
    fn: (param: PoeEventParameterTypes[E]) => void | Promise<void>,
    scope?: unknown,
    eOpts?: unknown
  ): void {
    const poe: PoeInterface | undefined = getPOE();
    if (undefined === poe) {
      console.warn('Unable to addEventListener as no POE is available!');
    } else {
      poe.addEventListener(
        this.componentID,
        evtName as string,
        fn,
        scope,
        eOpts
      );
    }
  }

  /**
   * Removes an event listener for the passed event.
   * @param evtName the name of the event to remove.
   */
  removeEventListener(evtName: string): void {
    const poe: PoeInterface | undefined = getPOE();
    if (undefined === poe) {
      console.warn('Unable to removeEventListener as no POE is available!');
    } else {
      poe.removeEventListener(this.componentID, evtName);
    }
  }

  /**
   * Fires an event to the POE, so that it is be distributed to registered handlers respectivley triggers a POE state change,
   * @param action the event name.
   * @param data the data to pass.
   */
  fireEvent<E extends keyof PoeEventParameterTypes>(
    action: E,
    data: PoeEventParameterTypes[E]
  ): void {
    const poe: PoeInterface | undefined = getPOE();
    if (undefined === poe) {
      console.warn('Unable to fire Event as no POE is available!');
    } else {
      poe.performAction(this.componentID, action as string, data);
    }
  }

  /**
   * Triggers a finish event action.
   *
   * @param data An optional data payload.
   */
  finish(data: unknown): void {
    this.fireEvent('finish', data);
  }
}
